import React, { useState } from "react";
import AuthService from "../../services/auth.service";
import UserService from "../../services/user.service";
import { Box, Text, Button, Collapsible } from "grommet";
import { FormDown, FormNext } from "grommet-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import i18n from "../../i18n";
import "./NavBar.scss";
import { useFetchHotel } from "./redux/hooks";
import { getOfferCapabilities } from "../../common/offerCapabilities";

export default function NavBar(props) {
  const {
    isLoxeAdmin,
    isUser,
    hasPasscodes,
    isTtlock,
    houseCleaningEnabled,
    whiteLabeled,
    isAdmin,
    onOpenAssistant,
    ...boxProps
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settingsPath = "/settings";

  const { hotel } = useFetchHotel();
  const caps = getOfferCapabilities(hotel);
  const menu = [
    [t("nav.home"), "/"],
    [t("nav.reservations"), "/guests"],
    [t("nav.conversations"), "/conversations"],
    // ...(isAdmin ? [["All Current in Group", "/guests/all-group"]] : []),
    ...(caps.accessDoors && hasPasscodes
      ? [[t("nav.passcodes"), "/current-passcodes"]]
      : []),
    ...(caps.accessDoors && isTtlock
      ? [[t("nav.unlockRecords"), "/unlock-records"]]
      : []),
    ...(caps.accessDoors && (isTtlock || hotel?.hasWifiGateway)
      ? [[t("nav.gateways"), "/gateways"]]
      : []),
    ...(caps.accessDoors && houseCleaningEnabled
      ? [[t("nav.housekeeping"), "/housekeeping"]]
      : []),
  ];

  const menuSettings = [
    [t("nav.hotelInfo"), "/info"],
    [t("nav.roomTypes"), "/roomtypes"],
    [caps.accessDoors ? t("nav.doorLocks") : t("offers.rooms"), "/doors"],
    [t("nav.times"), "/times"],
    ...(caps.accessDoors ? [[t("nav.checkinFlow"), "/checkin-flow"]] : []),
    [t("automation.title"), "/automation-flows"],
    [
      t("nav.aiTraining"),
      "/training",
      t("nav.aiTrainingGuestsBadge"),
      "#d211ef",
    ],
    [
      t("nav.aiTraining"),
      "/training-staff",
      t("nav.aiTrainingStaffBadge"),
      "#0095ff",
    ],
    [t("nav.upsellAds"), "/upsell"],
    [t("nav.upsellIntegrations"), "/upsell-integrations"],
    [t("nav.pmsIntegration"), "/pms"],
    [t("nav.users"), "/users"],
    ...(whiteLabeled ? [[t("nav.whitelabel"), "/whitelabel"]] : []),
    ...(isAdmin ? [[t("nav.billing"), "/billing"]] : []),
    ...(isLoxeAdmin ? [[t("nav.admin"), "/admin"]] : []),
  ];

  const [openMenuSettings, setOpenMenuSettings] = useState(
    window.location.pathname.includes(settingsPath),
  );

  const handleLogout = (e) => {
    e.preventDefault();
    AuthService.logout();
    window.location.href = "/sign-in";
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        user.language = lang;
        localStorage.setItem("user", JSON.stringify(user));
        UserService.updateLanguage(user.id || user._id, lang).catch(() => {});
      }
    } catch {
      // ignore
    }
  };

  const currentLang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const SettingsButton = ({ open, ...rest }) => {
    const Icon = open ? FormDown : FormNext;
    return (
      <Button
        hoverIndicator
        active={window.location.pathname.includes(settingsPath)}
        onClick={() => {
          setOpenMenuSettings(!open);
        }}
      >
        <Box
          pad={{ horizontal: "medium", vertical: "small" }}
          direction="row"
          justify="between"
        >
          <Text>{t("nav.settings")}</Text>
          <Icon color="white" />
        </Box>
      </Button>
    );
  };

  return (
    <Box
      {...boxProps}
      background="dark-3"
      width="225px"
      fill="vertical"
      justify="between"
    >
      <Box flex={{ grow: 1, shrink: 1 }} overflow={{ vertical: "auto" }}>
        {menu.map(([name, path]) => (
          <Button
            key={name}
            onClick={() => navigate(path)}
            hoverIndicator
            active={
              path === "/guests"
                ? ["/guests", "/expected", "/archived"].some(
                    (reservationPath) =>
                      window.location.pathname.startsWith(reservationPath),
                  )
                : path === window.location.pathname
            }
          >
            <Box pad={{ horizontal: "medium", vertical: "small" }}>
              <Text>{name}</Text>
            </Box>
          </Button>
        ))}
        {!isUser && (
          <>
            <SettingsButton key={settingsPath} open={openMenuSettings} />
            <Box background="background-contrast">
              <Collapsible open={openMenuSettings}>
                {menuSettings.map(([name, path, badge, badgeColor]) => (
                  <Button
                    key={path}
                    onClick={() => navigate(settingsPath + path)}
                    hoverIndicator
                    className="settings-button"
                    active={settingsPath + path === window.location.pathname}
                  >
                    <Box
                      pad={{ horizontal: "medium", vertical: "small" }}
                      direction="row"
                      align="center"
                      justify="between"
                      gap="small"
                    >
                      <Text>{name}</Text>
                      {badge && (
                        <Box
                          background={badgeColor}
                          round="xsmall"
                          pad={{ horizontal: "6px", vertical: "2px" }}
                        >
                          <Text size="11px" weight="bold" color="white">
                            {badge}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Button>
                ))}
              </Collapsible>
            </Box>
          </>
        )}
      </Box>
      <Box flex={false} background="dark-3">
        <Box
          direction="row"
          justify="center"
          gap="xsmall"
          pad={{ horizontal: "medium", vertical: "small" }}
        >
          <Button
            plain
            onClick={() => handleLanguageChange("en")}
            style={{ opacity: currentLang === "en" ? 1 : 0.4, fontSize: 18 }}
            title="English"
          >
            🇬🇧
          </Button>
          <Button
            plain
            onClick={() => handleLanguageChange("fr")}
            style={{ opacity: currentLang === "fr" ? 1 : 0.4, fontSize: 18 }}
            title="Français"
          >
            🇫🇷
          </Button>
        </Box>
        <Button
          key="logout"
          onClick={handleLogout}
          hoverIndicator
          className="menu-logout"
        >
          <Box pad={{ horizontal: "medium", vertical: "small" }}>
            <Text>{t("nav.logout")}</Text>
          </Box>
        </Button>
      </Box>
    </Box>
  );
}

NavBar.propTypes = {};
NavBar.defaultProps = {};
